import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../api/authFetch';
import { API_BASE } from '../../api/astrology';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';


interface CosmicPulseModalProps {
  onClose: () => void;
}

const CosmicPulseModal: React.FC<CosmicPulseModalProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [premiumRequired, setPremiumRequired] = useState(false);
  const { isPremium } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCosmicPulse();
  }, []);

  const fetchCosmicPulse = async () => {
    try {
      // Get user ID from auth context or fall back to empty (Lambda will extract from token)
      const userId = user?.userId || '';
      console.log('[CosmicPulse] Fetching for user:', userId);
      
      const response = await authFetch(`${API_BASE}/cosmic-pulse?user_id=${encodeURIComponent(userId)}`);
      console.log('[CosmicPulse] Response status:', response.status);
      
      // Check if response is OK before parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        
        if (errorData.error === 'premium_required') {
          setPremiumRequired(true);
          setReading(errorData);
          setLoading(false);
          return;
        }
        
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }
      
      // Defensive JSON parsing: handle empty/malformed responses from Lambda cold starts
      let data;
      try {
        const text = await response.text();
        if (!text || text.trim() === '') {
          throw new Error('Empty response from server. Please try again.');
        }
        data = JSON.parse(text);
      } catch (parseErr: any) {
        console.error('[CosmicPulse] JSON parse error:', parseErr);
        throw new Error(parseErr.message || 'Invalid server response. Please try again.');
      }

      if (data.error === 'premium_required') {
        setPremiumRequired(true);
        setReading(data); // preserve free_used/free_limit for display
        setLoading(false);
        return;
      }

      if (data.error) {
        setError(data.message || data.error);
        setLoading(false);
        return;
      }

      setReading(data);
      setLoading(false);
      
      // Mark as revealed for today and store weather summary
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(`zeal_cosmic_pulse_revealed_${today}`, 'true');
      if (data.weather) {
        localStorage.setItem(`zeal_cosmic_pulse_weather_${today}`, data.weather);
      }
    } catch (err: any) {
      console.error('Failed to fetch Cosmic Pulse:', err);
      setError(err.message || 'Failed to load reading');
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    onClose();
    navigate('/premium', { state: { feature: 'Cosmic Pulse' } });
  };

  // Section styling helper
  const sectionStyle = {
    marginBottom: '22px',
  };
  const labelStyle = {
    fontSize: '11px',
    color: 'rgba(201,168,76,0.55)',
    marginBottom: '8px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600 as const,
    letterSpacing: '1.5px',
    textTransform: 'uppercase' as const,
  };
  const bodyStyle = {
    fontSize: '15px',
    color: 'rgba(255,255,255,0.88)',
    fontFamily: 'Inter, sans-serif',
    lineHeight: '1.7',
  };

  return (
    <>
    <style>{`@keyframes cosmicHueShift { 0% { filter: hue-rotate(0deg) brightness(1.2) drop-shadow(0 0 8px currentColor); } 50% { filter: hue-rotate(180deg) brightness(1.3) drop-shadow(0 0 16px currentColor); } 100% { filter: hue-rotate(360deg) brightness(1.2) drop-shadow(0 0 8px currentColor); } }`}</style>
    
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        paddingTop: 'calc(env(safe-area-inset-top, 20px) + 72px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 20px) + 72px)',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(145deg, #0e0e14, #1a1a2e)',
          border: '1px solid rgba(201,168,76,0.3)',
          borderRadius: '24px',
          padding: '64px 28px 28px',
          maxWidth: '480px',
          width: '100%',

          boxShadow: '0 0 60px rgba(201,168,76,0.15)',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{
            fontSize: '32px',
            fontFamily: 'Cinzel, serif',
            fontWeight: '700',
            marginBottom: '6px',
            color: '#c9a84c',
            animation: 'cosmicHueShift 6s linear infinite',
          }}>
            Cosmic Pulse
          </div>
          <div style={{
            fontSize: '12px',
            color: 'rgba(201,168,76,0.5)',
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
          }}>
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
          }}>
            <style>
              {`
                @keyframes zeal-orbit { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes cosmicHueShift { 0% { filter: hue-rotate(0deg) brightness(1.2) drop-shadow(0 0 8px currentColor); } 50% { filter: hue-rotate(180deg) brightness(1.3) drop-shadow(0 0 16px currentColor); } 100% { filter: hue-rotate(360deg) brightness(1.2) drop-shadow(0 0 8px currentColor); } }
                @keyframes zeal-breathe { 0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(201,168,76,0.15); } 50% { transform: scale(1.08); box-shadow: 0 0 60px rgba(201,168,76,0.3); } }
              `}
            </style>
            <div style={{ position: 'relative', width: '120px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                position: 'absolute', inset: '-8px',
                border: '1px solid rgba(201,168,76,0.1)',
                borderTopColor: 'rgba(201,168,76,0.4)',
                borderRadius: '50%',
                animation: 'zeal-orbit 3s linear infinite',
              }} />
              <img
                src="/app-icon.jpg"
                alt="Zeal Genesis"
                style={{
                  width: '76px', height: '76px',
                  borderRadius: '50%',
                  border: '2px solid rgba(201,168,76,0.3)',
                  animation: 'zeal-breathe 2.5s ease-in-out infinite',
                }}
              />
            </div>
            <p style={{
              marginTop: '20px',
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: '13px',
              color: 'rgba(201,168,76,0.5)',
              letterSpacing: '1px',
            }}>
              Reading the sky...
            </p>
          </div>
        )}

        {/* Premium gate */}
        {!loading && premiumRequired && (
          <div style={{ textAlign: 'center', padding: '30px 16px' }}>
            <div style={{
              fontSize: '40px',
              marginBottom: '16px',
            }}>
              ✦
            </div>
            <div style={{
              fontSize: '18px',
              color: '#c9a84c',
              fontFamily: 'Cinzel, serif',
              fontWeight: 600,
              marginBottom: '10px',
            }}>
              Premium Feature
            </div>
            <div style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.5)',
              fontFamily: 'Inter, sans-serif',
              lineHeight: '1.6',
              marginBottom: '24px',
            }}>
              {reading?.free_used !== undefined
                ? `You've used your 2 free Cosmic Pulse readings this week. Upgrade to premium for unlimited daily readings.`
                : 'Unlock your personalized daily cosmic reading with a premium subscription. Start with a 7-day free trial.'}
            </div>
            <button
              onClick={handleUpgrade}
              style={{
                padding: '14px 32px',
                border: 'none',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #c9a84c, #a8882c)',
                color: '#0a0a0f',
                fontSize: '15px',
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '0.5px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Unlock Cosmic Pulse
            </button>
            <div style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.25)',
              marginTop: '10px',
              fontFamily: 'Inter, sans-serif',
            }}>
              Includes 7-day free trial
            </div>
          </div>
        )}

        {/* Error state */}
        {!loading && error && !premiumRequired && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'rgba(255,100,100,0.8)',
            fontFamily: 'Inter, sans-serif',
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Reading content — 4 layers */}
        {!loading && !error && !premiumRequired && reading && (
          <div>
            {/* Free readings remaining badge */}
            {reading.free_remaining !== undefined && !reading.is_premium && !isPremium && (
              <div
                onClick={handleUpgrade}
                style={{
                  textAlign: 'center',
                  marginBottom: '16px',
                  padding: '8px 16px',
                  background: reading.free_remaining <= 1 ? 'rgba(231,76,60,0.1)' : 'rgba(201,168,76,0.08)',
                  border: `1px solid ${reading.free_remaining <= 1 ? 'rgba(231,76,60,0.25)' : 'rgba(201,168,76,0.15)'}`,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = reading.free_remaining <= 1 ? 'rgba(231,76,60,0.15)' : 'rgba(201,168,76,0.12)';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = reading.free_remaining <= 1 ? 'rgba(231,76,60,0.1)' : 'rgba(201,168,76,0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span style={{
                  fontSize: '12px',
                  color: reading.free_remaining <= 1 ? 'rgba(231,76,60,0.8)' : 'rgba(201,168,76,0.7)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}>
                  {reading.free_remaining === 0
                    ? '✦ Last free reading this week — upgrade for unlimited'
                    : `✦ ${reading.free_remaining} free reading${reading.free_remaining === 1 ? '' : 's'} remaining this week`}
                </span>
              </div>
            )}
            {/* Daily Focus + Context subtitle */}
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
              padding: '10px 16px',
              background: 'rgba(201,168,76,0.06)',
              borderRadius: '12px',
              border: '1px solid rgba(201,168,76,0.1)',
            }}>
              {/* Daily Focus */}
              {reading.focus_planet && (
                <div style={{
                  fontSize: '13px',
                  color: 'rgba(201,168,76,0.9)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  marginBottom: '6px',
                }}>
                  Your {reading.focus_planet} in {reading.focus_natal_sign} · {
                    reading.focus_contrast_theme === 'moon_phase' ? `${reading.moon_phase_name} Moon` :
                    reading.focus_contrast_theme === 'season' ? `${reading.sun_sign} Season` :
                    reading.aspect_label
                  }
                </div>
              )}
              {/* Sky Aspect */}
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.4)',
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '0.5px',
              }}>
                {reading.aspect_label}
              </div>
              {/* Season & Decan */}
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.35)',
                fontFamily: 'Inter, sans-serif',
                marginTop: '4px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}>
                {reading.sun_sign} Season · {reading.decan === 1 ? '1st' : reading.decan === 2 ? '2nd' : '3rd'} Decan · {reading.decan_ruler}
              </div>
              {reading.moon_phase_name && (
                <div style={{
                  fontSize: '11px',
                  color: reading.moon_phase_major ? '#c9a84c' : 'rgba(255,255,255,0.35)',
                  fontFamily: 'Inter, sans-serif',
                  marginTop: '4px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontWeight: reading.moon_phase_major ? 600 : 400,
                }}>
                  {reading.moon_phase_major ? '✦ ' : ''}{reading.moon_phase_name}
                </div>
              )}
              {reading.seasonal_marker && (
                <div style={{
                  fontSize: '11px',
                  color: '#c9a84c',
                  fontFamily: 'Inter, sans-serif',
                  marginTop: '4px',
                  letterSpacing: '1px',
                  fontWeight: 600,
                }}>
                  ✦ {reading.seasonal_marker}
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.2), transparent)',
              marginBottom: '24px',
            }} />

            {/* THE WEATHER */}
            <div style={sectionStyle}>
              <div style={labelStyle}>The Weather</div>
              <div style={bodyStyle}>
                {reading.weather}
              </div>
            </div>

            {/* THE SHIFT */}
            <div style={sectionStyle}>
              <div style={labelStyle}>The Shift</div>
              <div style={bodyStyle}>
                {reading.shift}
              </div>
            </div>

            {/* THE PREDICTION */}
            <div style={{ ...sectionStyle, marginBottom: '8px' }}>
              <div style={labelStyle}>The Prediction</div>
              <div style={{
                ...bodyStyle,
                fontWeight: 500,
              }}>
                {reading.prediction}
              </div>
            </div>
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '14px',
            background: 'rgba(201,168,76,0.1)',
            border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: '12px',
            color: '#c9a84c',
            fontFamily: 'Inter, sans-serif',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(201,168,76,0.18)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(201,168,76,0.1)';
          }}
        >
          Close
        </button>
      </div>
    </div>
    </>
  );
};

export default CosmicPulseModal;
